import { Injectable, inject, Inject, InjectionToken, Injector } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { HttpClient } from '@angular/common/http';
import { runInInjectionContext } from '@angular/core';

declare var FFmpegWASM: any;

@Injectable({
  providedIn: 'root'
})
export class VideoTrimService {
  private messageSubject = new Subject<any>();
  private ffmpegRef: FFmpeg | null = null;
  private loadedSubject = new BehaviorSubject<boolean>(false);
  private progressSubject = new BehaviorSubject<number>(0);
  private transcodeLogSubject = new BehaviorSubject<string | null>(null);
  private loadPromise: Promise<void> | null = null;

  private http = inject(HttpClient);
  private injector = inject(Injector);

  protected baseURLCore = 'https://unpkg.com/@ffmpeg/core@0.12.3/dist/umd';
  protected baseURLFFMPEG = 'https://unpkg.com/@ffmpeg/ffmpeg@0.12.6/dist/umd';

  constructor() {
    // Initiate loading when the service is created
    // Removed runInInjectionContext wrapper as it might interfere with FFmpeg init timing
    this.load().catch(err => console.error("Initial FFmpeg load failed:", err));
  }

  getMessages(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  getLoaded(): Observable<boolean> {
    return this.loadedSubject.asObservable();
  }

  // Added method to check current loaded state synchronously
  isLoaded(): boolean {
    return this.loadedSubject.value;
  }

  getProgress(): Observable<number> {
    return this.progressSubject.asObservable();
  }

  getTranscodeLog(): Observable<string | null> {
    return this.transcodeLogSubject.asObservable();
  }

  private ensureLoaded(): Promise<void> {
    if (this.loadedSubject.value) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    // If load wasn't initiated or failed previously, try again
    console.log("ensureLoaded: Triggering FFmpeg load...");
    this.loadPromise = this.load();
    return this.loadPromise;
  }

  // Make load method public but primarily called by constructor/ensureLoaded
  async load(): Promise<void> {
    // Prevent multiple simultaneous loads
    if (this.loadPromise && !this.loadedSubject.value) { // Check if already loading
        console.log("FFmpeg load already in progress.");
        return this.loadPromise;
    }
    if (this.loadedSubject.value) {
        console.log("FFmpeg already loaded.");
        return Promise.resolve();
    }

    console.log("Starting FFmpeg load...");
    // Assign the promise *before* the async operation starts
    this.loadPromise = (async () => {
      console.log("FFmpeg load: Starting async IIFE.");
      try {
        console.log("FFmpeg load: Attempting to patch ffmpeg.js URL...");
        const ffmpegBlobURL = await this.toBlobURLPatched(`${this.baseURLFFMPEG}/ffmpeg.js`, 'text/javascript', (js: any) => {
          return js.replace('new URL(e.p+e.u(814),e.b)', 'r.worker814URL');
        });
        console.log("FFmpeg load: ffmpeg.js URL patched:", ffmpegBlobURL ? 'Success' : 'Failed');

        const config = {
          worker814URL: await toBlobURL(`${this.baseURLFFMPEG}/814.ffmpeg.js`, 'text/javascript', true),
          coreURL: await toBlobURL(`${this.baseURLCore}/ffmpeg-core.js`, 'text/javascript', true),
          wasmURL: await toBlobURL(`${this.baseURLCore}/ffmpeg-core.wasm`, 'application/wasm', true),
          simd: true, // Assuming SIMD is desired/supported
          // optimizations: ['-msimd128', '-fno-rtti', '-fno-exceptions'] // Optional optimizations
        };
        console.log("FFmpeg load: Blob URLs created. Config:", config);

        // Dynamically import the patched ffmpeg.js
        console.log("FFmpeg load: Attempting dynamic import of ffmpeg.js...");
        await import(/* @vite-ignore */ffmpegBlobURL);
        console.log("FFmpeg load: Dynamic import successful.");


        // Check if FFmpegWASM is available on window
        console.log("FFmpeg load: Checking for FFmpegWASM global...");
        if (typeof FFmpegWASM === 'undefined' || !FFmpegWASM.FFmpeg) {
            console.error('FFmpeg load: FFmpegWASM global object not found!'); // Log specific error
            throw new Error('FFmpegWASM global object not found after import.');
        }
        console.log("FFmpeg load: FFmpegWASM global found.");


        console.log("FFmpeg load: Instantiating FFmpeg...");
        this.ffmpegRef = new FFmpegWASM.FFmpeg();
        console.log("FFmpeg load: FFmpeg instantiated.");

        // Use optional chaining for event listeners
        this.ffmpegRef?.on('log', ({ message }: { message: string }) => {
          console.log("FFmpeg Log:", message);
          this.transcodeLogSubject.next(message);
        });
        this.ffmpegRef?.on('progress', ({ progress }: { progress: number }) => {
          this.progressSubject.next(Math.ceil(progress * 100));
        });

        console.log("FFmpeg load: Calling ffmpegRef.load()...");
        // Add null check before calling load
        if (!this.ffmpegRef) {
             console.error("FFmpeg load: ffmpegRef is null before calling load!");
            throw new Error("FFmpeg reference became null unexpectedly after initialization.");
        }
        await this.ffmpegRef.load(config);
        console.log("FFmpeg load: ffmpegRef.load() completed.");


        console.log('FFmpeg load: Success - setting loadedSubject to true');
        this.loadedSubject.next(true);
        // Keep loadPromise resolved so ensureLoaded works correctly after success
      } catch (error) {
        console.error('FFmpeg load: Detailed error during load:', error); // Log the full error object
        this.loadedSubject.next(false); // Ensure status is false on error
        this.loadPromise = null; // Allow retry on error by resetting the promise
        throw error; // Re-throw error
      }
    })(); // Immediately invoke the async IIFE

    return this.loadPromise;
  }

  async trimVideo(videoBlob: Blob, startTime: number, endTime: number, originalFilename?: string): Promise<{ blob: Blob, fileName: string }> {
    return runInInjectionContext(this.injector, async () => {
      await this.ensureLoaded(); // Ensure FFmpeg is loaded before trimming

      if (!this.ffmpegRef) {
        throw new Error('FFmpeg is not loaded or failed to initialize.');
      }

      const inputFileName = 'input.mp4'; // Consistent input name

      // Generate output filename based on original filename if provided
      let outputFileName: string;
      if (originalFilename) {
        // Extract just the base filename without path
        const baseFilename = originalFilename.split('/').pop() || originalFilename;
        // Remove extension if present
        const nameWithoutExt = baseFilename.replace(/\.[^/.]+$/, '');
        // Create new filename with _trimmed suffix
        outputFileName = `${nameWithoutExt}_trimmed.mp4`;
      } else {
        // Fallback to timestamp-based naming if no original filename
        const timestamp = new Date().getTime();
        outputFileName = `trimmed_video_${timestamp}.mp4`;
      }

      console.log(`Writing input file: ${inputFileName}`);
      // Use optional chaining or throw if null (ensureLoaded should prevent this)
      await this.ffmpegRef!.writeFile(inputFileName, await fetchFile(videoBlob));
      console.log(`Input file written. Executing trim command for ${outputFileName}...`);

      const duration = endTime - startTime;
      if (duration <= 0) {
          throw new Error(`Invalid trim duration: ${duration}. End time must be after start time.`);
      }

      await this.ffmpegRef.exec([
        '-ss', startTime.toString(), // Start time offset
        '-i', inputFileName,        // Input file
        '-to', endTime.toString(),  // End time position
        // '-t', duration.toString(), // Alternative: duration
        '-c', 'copy',               // Copy codecs (faster if possible)
        // '-preset', 'ultrafast',  // Preset might not be needed with codec copy
        '-movflags', '+faststart',  // Optimize for web streaming
        '-y',                       // Overwrite output file without asking
        outputFileName
      ]);
      console.log(`FFmpeg exec command finished for ${outputFileName}. Reading output file...`);


      // Use optional chaining or throw if null
      const data = await this.ffmpegRef!.readFile(outputFileName);
      console.log(`Output file ${outputFileName} read successfully.`);

      let blob: Blob;
      // FFmpeg v0.11+ returns Uint8Array
      if (data instanceof Uint8Array) {
        blob = new Blob([data.buffer], { type: 'video/mp4' });
      } else {
         // Handle potential older versions or unexpected types (though unlikely with current setup)
         console.warn('Unexpected data type received from FFmpeg, attempting conversion:', typeof data);
         try {
            // Attempt to create blob assuming it might be array-like or string
            blob = new Blob([data as any], { type: 'video/mp4' });
         } catch (conversionError) {
             console.error("Failed to convert FFmpeg output to Blob:", conversionError);
             throw new Error('Unexpected data type received from FFmpeg and failed to convert.');
         }
      }


      console.log(`Trimmed video created: ${outputFileName}, Size: ${blob.size} bytes`);

      // Optional: Clean up the input file in the virtual FS if needed
      // await this.ffmpegRef.deleteFile(inputFileName);
      // await this.ffmpegRef.deleteFile(outputFileName); // Usually not needed as readFile gets the data

      return { blob, fileName: outputFileName };
    });
  }

  // Patched toBlobURL to handle specific replacement needed for FFmpeg UMD build
  private toBlobURLPatched(url: string, mimeType: string, patcher: (js: string) => string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.get(url, { responseType: 'text' }).subscribe({
        next: (js) => {
          try {
            const patchedJS = patcher(js);
            const blob = new Blob([patchedJS], { type: mimeType });
            resolve(URL.createObjectURL(blob));
          } catch (patchError) {
            console.error("Error patching JS for Blob URL:", patchError);
            reject(patchError);
          }
        },
        error: (err) => {
            console.error(`Error fetching ${url}:`, err);
            reject(err);
        }
      });
    });
  }

  // Kept for potential future use, but not directly used by trim service itself
  loadFile(url: string): Observable<any> {
    return this.http.get(url, {
      responseType: 'arraybuffer',
      reportProgress: true,
      observe: 'events'
    });
  }
}
